import React from 'react';
import { CloseButton, Textarea as MantineTextarea, TextareaProps } from '@mantine/core';
import { useMergedRef } from '@mantine/hooks';
import clsx from 'clsx';
import { toMantineError } from '../../theme/forms';
import { describedByContainer } from '../../theme/describedByContainer';
import { useFieldA11y } from '../../hooks/useFieldA11y';
import { useUi } from '../../provider/UiContext';
import TextareaAiAssist from './TextareaAiAssist';
import './Textarea.scss';

// Фасад на Mantine Textarea. Публичный API сохранён ради потребителей (TextareaControl,
// TextareaBase64Control ×13, движок форм Field): legacy-пропы autoResize/initialRowCount/
// invalid/onValueChange/testid маппятся в нативные Mantine внутри. Самодельный rxjs-debounce
// (fromEvent/debounceTime) и CSS-трюк авто-высоты (data-replicated-value) сняты — авто-высоту
// даёт нативный autosize, рамку/фокус/readonly — глобальная тема common.scss (.mantine-Input-input).
// Mantine-aligned переименования (autosize/minRows/error/onChange) — в общий codemod кластера.
export interface ITextareaProps extends Omit<TextareaProps, 'onChange' | 'size' | 'error' | 'value'> {
    /** Ссылка на textarea-элемент */
    ref?: React.Ref<HTMLTextAreaElement>;
    /** Авто-высота по контенту (legacy, → Mantine autosize). @default false */
    autoResize?: boolean;
    /** Минимальное число строк (legacy, → Mantine minRows/rows). @default 3 */
    initialRowCount?: number;
    /** Невалидное состояние (мост invalid→error через toMantineError). */
    invalid?: boolean;
    /** Кнопка очистки (CloseButton-оверлей при непустом значении). */
    onClear?: () => void;
    /** Нативный onChange (event). */
    onChange?: React.ChangeEventHandler<HTMLTextAreaElement>;
    /** Контролируемое значение; null из форм нормализуется в пустую строку. */
    value?: TextareaProps['value'] | null;
    /** Нормализованное значение строкой (контракт кластера, несёт RHF-интеграцию в Base64Control). */
    onValueChange?: (value: string) => void;
    /** Размер по шкале дизайн-системы @default 'sm' */
    size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /**
     * @deprecated Легаси-testid: рендерит data-testid="textarea-${testid}". Уйдёт в едином
     * testid→data-testid пассе. Пока сохранён для e2e.
     */
    testid?: string;
    /**
     * Ключ AI-улучшения текста (TextareaAiAssist, доменное расширение, не Mantine). Кнопка улучшения есть, только если
     * приложение передало `aiTransform` в `UiProvider`.
     */
    aiAutocompleteKey?: string;
}

const Textarea: React.FC<ITextareaProps> = ({
    ref,
    className = '',
    autoResize = false,
    initialRowCount = 3,
    invalid,
    onClear,
    onChange,
    onValueChange,
    onFocus,
    onBlur,
    onKeyDown,
    size = 'sm',
    testid,
    aiAutocompleteKey,
    value,
    defaultValue,
    disabled,
    readOnly,
    maxLength,
    autoFocus,
    // Нативный rows атом исторически игнорировал (перетирал высотой по initialRowCount) —
    // сохраняем паритет, чтобы rows={6} у модалок не менял высоту молча.
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    rows: _rows,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    inputContainer,
    ...rest
}) => {
    const { aiTransform } = useUi();
    const fieldA11y = useFieldA11y(
        { 'aria-label': rest['aria-label'], 'aria-labelledby': ariaLabelledBy, 'aria-describedby': ariaDescribedBy },
        !!rest.label,
    );
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const mergedRef = useMergedRef(ref, innerRef);
    const normalizedValue = value === null ? '' : value;

    // Длина для счётчика maxLength: seed из value/defaultValue, обновляем на вводе (работает и
    // в controlled, и в uncontrolled). Считаем от НОРМАЛИЗОВАННОГО значения: при value=null в поле
    // пусто, и счётчик не должен показывать длину defaultValue.
    const seedLen = String(normalizedValue ?? defaultValue ?? '').length;
    const [len, setLen] = React.useState<number>(seedLen);
    React.useEffect(() => {
        if (normalizedValue !== undefined) setLen(String(normalizedValue).length);
    }, [normalizedValue]);

    /** Идёт AI-улучшение — для слоя дышащего свечения позади поля */
    const [aiBusy, setAiBusy] = React.useState(false);

    React.useEffect(() => {
        if (autoFocus && innerRef.current) innerRef.current.focus({ preventScroll: true });
    }, [autoFocus]);

    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (e) => {
        onChange?.(e);
        onValueChange?.(e.currentTarget.value);
        setLen(e.currentTarget.value.length);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
        if (e.key === 'Enter') {
            e.stopPropagation();
        }
        onKeyDown?.(e);
    };

    // Программная установка значения с прогоном через React onChange (для AI/clear): нативный
    // сеттер + input-событие триггерит handleChange → onChange/onValueChange, и контролируемое
    // value обновляется через родителя (RHF), не ломая controlled-контракт.
    const propagateValue = (val: string) => {
        const el = innerRef.current;
        if (!el) return;
        // нативный value-сеттер вызываем через .call(el) — this привязан явно (не unbound).
        // eslint-disable-next-line @typescript-eslint/unbound-method
        const setter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value')?.set;
        setter?.call(el, val);
        el.dispatchEvent(new Event('input', { bubbles: true }));
    };

    const clearInput = () => {
        propagateValue('');
        onClear?.();
    };

    const heightProps: Partial<TextareaProps> = autoResize
        ? { autosize: true, minRows: initialRowCount }
        : { rows: initialRowCount };

    const field = (
        <MantineTextarea
            {...rest}
            aria-labelledby={fieldA11y['aria-labelledby']}
            inputContainer={describedByContainer(fieldA11y['aria-describedby'], inputContainer)}
            ref={mergedRef}
            // Легаси-хуки слотов: внешние стили (widgets/Text, TableData) таргетят
            // .rf-textarea__wrapper/.rf-textarea__field — маппим их на слоты Mantine;
            // rf-textarea__root — новый узел (InputWrapper-root, у легаси не было).
            classNames={{
                root: 'rf-textarea__root',
                wrapper: 'rf-textarea__wrapper',
                input: 'rf-textarea__field',
            }}
            value={normalizedValue}
            defaultValue={defaultValue}
            size={size}
            disabled={disabled}
            readOnly={readOnly}
            maxLength={maxLength}
            error={toMantineError(invalid)}
            onChange={handleChange}
            onFocus={onFocus}
            onBlur={onBlur}
            onKeyDown={handleKeyDown}
            {...heightProps}
            {...(testid ? { 'data-testid': `textarea-${testid}` } : {})}
        />
    );

    return (
        <div className={clsx('rf-textarea', className)}>
            {aiAutocompleteKey && aiTransform ? (
                // Хост AI-оверлеев: его бокс совпадает с боксом Mantine-поля, поэтому
                // ring/reveal (inset:0) ложатся точно по рамке поля, а glow — ореолом позади.
                <div className="rf-textarea__ai-host">
                    <div className={clsx('rf-ai-glow', aiBusy && 'is-on')} aria-hidden="true" />
                    {field}
                    <TextareaAiAssist
                        aiKey={aiAutocompleteKey}
                        transform={aiTransform}
                        getText={() => innerRef.current?.value ?? ''}
                        onResult={propagateValue}
                        onBusyChange={setAiBusy}
                    />
                </div>
            ) : (
                field
            )}

            {maxLength !== undefined && maxLength > 0 && (
                <div className="rf-textarea__max-length">
                    Осталось символов: {maxLength - len}/{maxLength}
                </div>
            )}

            {onClear && len > 0 && !readOnly && !disabled && (
                <CloseButton className="rf-textarea__action" aria-label="Сбросить" onClick={clearInput} />
            )}
        </div>
    );
};

export default Textarea;
